// src/pages/AIPage.js – نسخهٔ نهایی با پشتیبانی از تاریخچهٔ چت، کارت‌ها و Thinking Steps

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { BsSend, BsCopy, BsSearch, BsLightbulb } from 'react-icons/bs';
import { Helmet } from 'react-helmet-async';
import VerticalContentCard from '../components/Category/VerticalContentCard';
import BookCard from '../components/library/BookCard';

const BG_IMAGE = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/AI.png';

const TOPBAR_HEIGHT = 56;
const BOTTOMNAV_HEIGHT = 64;

const initialMessage = `سلام. من دستیار هوشمند «فرزانه» هستم، طراحی شده‌ام تا شما را در درک عمیق‌تر دیدگاه‌ها و آثار استاد مصطفی ملکیان همراهی کنم. من با دسترسی به سخنرانی‌ها، کتاب‌ها و مقالات مرتبط، جامع‌ترین پاسخ‌ها را ارائه می‌دهم.

شما می‌توانید در مورد این موارد از من بپرسید:
* مفاهیم اصلی در فلسفه ملکیان (مانند خردورزی، اخلاق، معنویت).
* مطالب ارائه شده در درس‌گفتارها و دوره‌های سایت.
* توصیه کتاب‌ها و مقالات مرتبط با موضوع مورد نظر شما.

برای مثال:
* «در کدام سخنرانی‌ها و درس گفتارهای استاد ملکیان به آثار میشل فوکو اشاره شده است؟»
* «کدام آثار استاد در ارتباط با روانشناسی مثبت نگر است؟»
* «رابطه عقلانیت و معنویت از دیدگاه ایشان چیست؟»

لطفاً پرسش خود را مطرح کنید.`;

// ── Regex ها و توابع کمکی (بدون تغییر) ──
const CARD_LINK_REGEX = /(?:🎓|📚|🎙️|-)?\s*\[([^\]]+)\]\((\/(?:products|books|media)\/[^)]+)\)/g;
const PAGE_LINK_REGEX = /\[([^\]]+)\]\((\/[^)]+)\)/g;

function stripHtml(str) { return str.replace(/<[^>]*>/g, ''); }

function parseMessageContent(rawContent) {
  const content = stripHtml(rawContent);
  const blocks = [];
  const lines = content.split('\n');
  let textBuffer = [];
  let currentCards = [];

  const flushText = () => {
    const txt = textBuffer.join('\n').trim();
    if (txt) blocks.push({ type: 'text', value: txt });
    textBuffer = [];
  };
  const flushCards = () => {
    if (currentCards.length) {
      blocks.push({ type: 'cards', items: currentCards });
      currentCards = [];
    }
  };

  for (const line of lines) {
    CARD_LINK_REGEX.lastIndex = 0;
    const cardMatch = CARD_LINK_REGEX.exec(line);
    if (cardMatch) {
      flushText();
      currentCards.push({
        title: cardMatch[1].trim(),
        url: cardMatch[2].trim(),
        itemType: cardMatch[2].startsWith('/books/') ? 'book' : 'product',
      });
      continue;
    }
    flushCards();
    PAGE_LINK_REGEX.lastIndex = 0;
    const pageMatch = PAGE_LINK_REGEX.exec(line);
    if (pageMatch) {
      const before = line.slice(0, pageMatch.index).trim();
      if (before) textBuffer.push(before);
      flushText();
      blocks.push({ type: 'link', title: pageMatch[1].trim(), url: pageMatch[2].trim() });
      const after = line.slice(pageMatch.index + pageMatch[0].length).trim();
      if (after) textBuffer.push(after);
    } else {
      textBuffer.push(line);
    }
  }
  flushText();
  flushCards();
  return blocks;
}

function extractSlug(url) {
  const parts = url.split('/').filter(Boolean);
  return parts[parts.length - 1] || '';
}

// ── هوک دریافت داده کارت‌ها ──
function useCardData(items, token) {
  const [cardData, setCardData] = useState({});
  const [loading, setLoading] = useState(false);
  const cardDataRef = useRef(cardData);
  useEffect(() => { cardDataRef.current = cardData; }, [cardData]);

  const itemsKey = items ? items.map(i => i.url).join(',') : '';

  useEffect(() => {
    if (!items || items.length === 0 || !token) return;

    const missing = items.filter(
      item => !cardDataRef.current[`${item.itemType}:${item.url}`]
    );
    if (missing.length === 0) return;

    setLoading(true);
    const payload = missing.map(item => ({
      type: item.itemType === 'book' ? 'book' : 'product',
      slug: extractSlug(item.url),
      title: item.title,
    }));

    fetch('https://farzaneh.app/api/v1/ai/cards/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ items: payload }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data || !data.results) return;
        const map = {};
        missing.forEach((item, idx) => {
          const result = data.results[idx];
          if (result) map[`${item.itemType}:${item.url}`] = result;
        });
        setCardData(prev => ({ ...prev, ...map }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [itemsKey, token]);

  return { cardData, loading };
}

// ── بلوک کارت‌ها ──
const CardsBlock = ({ items, token }) => {
  const { cardData } = useCardData(items, token);

  return (
    <div className="flex flex-col gap-3 my-3 w-full">
      {items.map((item, idx) => {
        const key = `${item.itemType}:${item.url}`;
        const data = cardData[key] || {
          type: item.itemType,
          slug: extractSlug(item.url),
          title: item.title,
          cover_url: null,
          url: item.url,
        };

        if (item.itemType === 'book') {
          return (
            <div key={idx} className="w-full">
              <BookCard
                book={{
                  slug: data.slug || extractSlug(item.url),
                  title: data.title || item.title,
                  cover_image_url: data.cover_url || '',
                  author: data.author ? { name: data.author } : null,
                  translator: data.translator ? { name: data.translator } : null,
                  publisher: data.publisher ? { title: data.publisher } : null,
                  tags: data.tags || [],
                }}
              />
            </div>
          );
        }

        return (
          <div key={idx} className="w-full">
            <VerticalContentCard
              data={{
                title: data.title || item.title,
                cover_url: data.cover_url || null,
                price_display: data.price_display || '',
                is_free: data.is_free || false,
                description: data.description || '',
                slug: data.slug || extractSlug(item.url),
                product_url: data.url || item.url,
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

// ── لینک قابل‌کلیک ──
const PageLink = ({ title, url }) => (
  <Link
    to={url}
    className="inline-flex items-center gap-1 text-blue-600 underline underline-offset-2 hover:text-blue-800 transition-colors text-sm"
  >
    {title}
  </Link>
);

// ── نمایش مراحل تفکر (Glassmorphism) ──
const ThinkingSteps = ({ steps }) => {
  if (!steps || steps.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5 mb-2 text-xs text-gray-700">
      {steps.map((s, i) => (
        <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-full border border-white/30 shadow-sm">
          <span className="text-base">{s.emoji || '🧠'}</span>
          <span>{s.content}</span>
        </div>
      ))}
    </div>
  );
};

// ── حباب پیام (User / Assistant) ──
const MessageBubble = ({ message, getToken }) => {
  const isUser = message.role === 'user';
  const blocks = isUser
    ? [{ type: 'text', value: message.content }]
    : parseMessageContent(message.content);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={[
          'w-fit max-w-[88%] rounded-2xl px-4 py-3 shadow-lg',
          isUser
            ? 'bg-white/40 backdrop-blur-lg border border-white/50 text-gray-900 rounded-br-sm'
            : 'bg-white/30 backdrop-blur-lg border border-blue-200/50 neon-glow text-gray-800 rounded-bl-sm'
        ].join(' ')}
      >
        <div className="flex justify-between items-center mb-1.5 text-[11px] opacity-60 gap-4">
          <span className="font-semibold">{isUser ? 'شما' : 'فرزانه'}</span>
          {!isUser && message.content && (
            <button
              onClick={() => copyToClipboard(message.content)}
              className="hover:opacity-100"
              aria-label="کپی"
            >
              <BsCopy size={12} />
            </button>
          )}
        </div>

        {!isUser && message.thinkingSteps && (
          <ThinkingSteps steps={message.thinkingSteps} />
        )}

        {/* نمایش مستقیم کارت‌های ارسالی از سرور */}
        {!isUser && message.cards && message.cards.length > 0 && (
          <CardsBlock
            items={message.cards.map(c => ({
              url: c.uri || c.url,
              title: c.title,
              itemType: c.type === 'book' ? 'book' : 'product',
            }))}
            token={getToken()}
          />
        )}

        {/* نمایش متن (شامل کارت‌های استخراج‌شده از مارک‌داون) */}
        <div className="text-[12px] leading-7 font-light text-right">
          {blocks.map((block, i) => {
            if (block.type === 'cards') {
              return <CardsBlock key={i} items={block.items} token={getToken()} />;
            }
            if (block.type === 'link') {
              return <PageLink key={i} title={block.title} url={block.url} />;
            }
            return (
              <div
                key={i}
                className="whitespace-pre-wrap break-words"
                style={{ overflowWrap: 'anywhere' }}
              >
                {block.value}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── صفحه Welcome (بدون تغییر) ──
const WelcomeScreen = ({ onSend }) => {
  const [query, setQuery] = useState('');

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (query.trim()) {
      onSend(query);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full px-4 text-center">
      <h1 className="text-3xl font-bold text-gray-800 mb-4">دستیار هوشمند فرزانه</h1>
      <p className="text-sm text-gray-600 max-w-md mb-8 leading-relaxed">
        {initialMessage.split('\n\n')[0]}
      </p>
      <div className="w-full max-w-lg relative">
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder="پرسش خود را بنویسید..."
          rows={2}
          className="w-full min-h-[56px] px-5 py-4 bg-white/40 backdrop-blur-md border border-gray-300/60 rounded-2xl outline-none transition-all text-sm resize-none text-gray-800 placeholder:text-gray-500 shadow-lg"
          disabled={false}
        />
        <button
          onClick={handleSubmit}
          disabled={!query.trim()}
          className={`absolute bottom-5 left-3 h-10 w-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
            query.trim()
              ? 'bg-violet-600 hover:bg-purple-700 text-white shadow-md shadow-purple-200 active:scale-95'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
          aria-label="ارسال"
        >
          <BsSend size={16} />
        </button>
      </div>
    </div>
  );
};

// ── کامپوننت اصلی ──
const AIPage = () => {
  const { isLoggedIn, openAuthModal, getAuthTokens } = useAuthContext();
  const navigate = useNavigate();

  const [chatStarted, setChatStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchWeb, setSearchWeb] = useState(false);
  const [deepSearch, setDeepSearch] = useState(true);
  const [thinkingSteps, setThinkingSteps] = useState([]);

  // -------- Session ID برای تاریخچهٔ چت --------
  const sessionIdRef = useRef(
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `session-${Date.now()}`
  );

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);

  const isMobile = window.innerWidth < 768;

  const [containerRect, setContainerRect] = useState(null);
  useEffect(() => {
    if (isMobile) return;
    const update = () => {
      const appContainer =
        document.querySelector('#root > div') ||
        document.querySelector('.app-container') ||
        document.getElementById('root');
      if (appContainer) {
        setContainerRect(appContainer.getBoundingClientRect());
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isMobile]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinkingSteps]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 140) + 'px';
  }, [input]);

  // -------- دریافت توکن --------
  const getToken = useCallback(() => {
    try {
      if (getAuthTokens && typeof getAuthTokens === 'function') {
        const tokens = getAuthTokens();
        if (tokens && tokens.access) return tokens.access;
        if (tokens && typeof tokens === 'string') return tokens;
      }
      return localStorage.getItem('access_token') || localStorage.getItem('token');
    } catch (e) {
      return null;
    }
  }, [getAuthTokens]);

  // -------- ذخیرهٔ پیام در سرور --------
  const persistMessage = useCallback(async (role, content) => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch('https://farzaneh.app/api/v1/ai/messages/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          role: role,
          content: content,
        }),
      });
    } catch (e) {
      console.error('Failed to persist message', e);
    }
  }, [getToken]);

  // -------- بارگذاری تاریخچهٔ چت در mount --------
  useEffect(() => {
    const loadHistory = async () => {
      if (!isLoggedIn) return;
      const token = getToken();
      if (!token) return;

      try {
        const response = await fetch('https://farzaneh.app/api/v1/ai/messages/', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!Array.isArray(data) || data.length === 0) return;

        // گروه‌بندی بر اساس session_id
        const sessions = {};
        data.forEach(msg => {
          const sid = msg.session_id;
          if (!sessions[sid]) sessions[sid] = [];
          sessions[sid].push(msg);
        });

        // یافتن آخرین session (نزولی بر اساس created_at)
        const sortedSessions = Object.entries(sessions).sort((a, b) => {
          const lastA = a[1][a[1].length - 1].created_at;
          const lastB = b[1][b[1].length - 1].created_at;
          return new Date(lastB) - new Date(lastA);
        });

        if (sortedSessions.length > 0) {
          const [latestSessionId, msgs] = sortedSessions[0];
          sessionIdRef.current = latestSessionId;   // ادامهٔ همان جلسه
          const formatted = msgs.map(msg => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            thinkingSteps: [],
            cards: null,
          }));
          setMessages(formatted);
          setChatStarted(true);
        }
      } catch (error) {
        console.error('Failed to load chat history', error);
      }
    };

    loadHistory();
  }, [isLoggedIn, getToken]);

  const sendMessage = async (userMessage) => {
    if (!isLoggedIn) { openAuthModal(); return; }
    const token = getToken();
    if (!token) { openAuthModal(); return; }
    if (!userMessage.trim()) return;

    if (!chatStarted) {
      setChatStarted(true);
    }

    const userMsgObj = { id: Date.now(), role: 'user', content: userMessage.trim() };
    setMessages(prev => [...prev, userMsgObj]);
    // ذخیره پیام کاربر در سرور
    persistMessage('user', userMessage.trim());

    const assistantMsgId = Date.now() + 1;
    const assistantMsg = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      thinkingSteps: [],
      cards: null,
    };
    setMessages(prev => [...prev, assistantMsg]);
    setIsLoading(true);
    setInput('');
    setThinkingSteps([]);

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const response = await fetch('https://farzaneh.app/api/v1/ai/api/chat/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          search_web: searchWeb,
          deep_search: deepSearch,
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        if (response.status === 401) {
          openAuthModal();
          throw new Error('نشست منقضی شده. دوباره وارد شوید.');
        }
        throw new Error(`خطای سرور: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      const currentThinking = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            try {
              const parsed = JSON.parse(data);
              if (parsed.type === 'meta') continue;

              if (parsed.type === 'status') {
                const step = {
                  emoji: parsed.emoji || (parsed.content.substring(0, parsed.content.indexOf(' ')) || '🧠'),
                  content: parsed.content,
                };
                currentThinking.push(step);
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMsgId
                    ? { ...msg, thinkingSteps: [...currentThinking] }
                    : msg
                ));
                setThinkingSteps([...currentThinking]);
                continue;
              }

              // پشتیبانی از type: 'sources' یا 'cards'
              if (parsed.type === 'sources' || parsed.type === 'cards') {
                const cardsData = parsed.data || [];
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMsgId
                    ? { ...msg, cards: cardsData.map(c => ({
                        uri: c.uri || c.url,
                        title: c.title,
                        type: c.type || (c.uri?.includes('/books/') ? 'book' : 'product')
                      }))
                    }
                    : msg
                ));
                continue;
              }

              if (parsed.text) {
                accumulatedText += parsed.text;
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMsgId
                    ? { ...msg, content: accumulatedText }
                    : msg
                ));
              }
            } catch (e) { /* ignore */ }
          }
        }
      }

      setMessages(prev => prev.map(msg =>
        msg.id === assistantMsgId
          ? { ...msg, thinkingSteps: currentThinking }
          : msg
      ));
      // ذخیره پاسخ نهایی دستیار در سرور
      if (accumulatedText) {
        persistMessage('assistant', accumulatedText);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMsgId
          ? { ...msg, content: `⚠️ خطا: ${error.message}` }
          : msg
      ));
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      setThinkingSteps([]);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (input.trim() && !isLoading) {
      sendMessage(input);
    }
  };

  // ── استایل‌های پورتال ──
  let portalStyle;
  if (isMobile) {
    portalStyle = {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 40,
      overflow: 'hidden',
    };
  } else if (containerRect) {
    portalStyle = {
      position: 'fixed',
      top: containerRect.top,
      left: containerRect.left,
      width: containerRect.width,
      height: containerRect.height,
      zIndex: 40,
      overflow: 'hidden',
    };
  } else {
    return <div style={{ width: '100vw', height: '100vh' }} />;
  }

  const innerContainerStyle = isMobile
    ? {
        paddingTop: TOPBAR_HEIGHT,
        paddingBottom: `calc(env(safe-area-inset-bottom, 16px) + ${BOTTOMNAV_HEIGHT}px + 16px)`,
        boxSizing: 'border-box',
      }
    : {};

  return ReactDOM.createPortal(
    <>
      <Helmet>
        <title>دستیار هوشمند | فرزانه</title>
      </Helmet>

      <style>{`
        @keyframes neonPulse {
          0%, 100% { box-shadow: 0 0 8px rgba(172, 138, 220, 0.09), 0 0 20px rgba(96, 165, 250, 0.09); }
          50% { box-shadow: 0 0 15px rgba(125, 58, 164, 0.1), 0 0 30px rgba(96, 165, 250, 0.18); }
        }
        .neon-glow { animation: neonPulse 3s ease-in-out infinite; }
        @keyframes neonPinkPurple {
          0%, 100% { 
            box-shadow: 0 0 8px rgba(236, 72, 154, 0.3), 0 0 16px rgba(168, 85, 247, 0.4);
            border-color: rgba(176, 89, 133, 0.45);
          }
          50% { 
            box-shadow: 0 0 16px rgba(230, 72, 236, 0.39), 0 0 30px rgba(169, 85, 247, 0.3);
            border-color: rgba(169, 85, 247, 0.46);
          }
        }
        .neon-pink-purple {
          animation: neonPinkPurple 2.5s ease-in-out infinite;
          border-width: 2px;
          border-style: solid;
        }
      `}</style>

      <div style={portalStyle}>
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${BG_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          }}
        />
        <div className="absolute inset-0 bg-white/30 backdrop-blur-sm" style={{ zIndex: 1 }} />

        <div
          className="relative flex flex-col h-full w-full"
          style={{ zIndex: 2, ...innerContainerStyle }}
        >
          {!chatStarted ? (
            <WelcomeScreen onSend={sendMessage} />
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-2 py-4">
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <MessageBubble key={msg.id} message={msg} getToken={getToken} />
                  ))}

                  {isLoading && thinkingSteps.length > 0 && (
                    <div className="flex justify-start">
                      <div className="bg-white/30 backdrop-blur-md rounded-2xl px-4 py-3 border border-white/20 shadow-sm w-fit">
                        <ThinkingSteps steps={thinkingSteps} />
                      </div>
                    </div>
                  )}
                  {isLoading && thinkingSteps.length === 0 && (
                    <div className="flex justify-start">
                      <div className="bg-white/40 backdrop-blur-md rounded-2xl px-4 py-3 border border-white/30 shadow-sm">
                        <span className="text-sm text-gray-600">در حال پردازش...</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div
                className="shrink-0 backdrop-blur-xl bg-white/40 border-t border-white/20 px-3 pt-3"
                style={{
                  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
                }}
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-end gap-2">
                    <textarea
                      ref={textareaRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmit(e);
                        }
                      }}
                      placeholder="سوال خود را اینجا بنویسید…"
                      rows={1}
                      className="flex-1 min-h-[48px] max-h-[140px] px-4 py-3 bg-white/30 backdrop-blur-md border border-white/30 rounded-2xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 transition-all text-sm resize-none leading-6 placeholder:text-gray-500 text-gray-800"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={!input.trim() || isLoading}
                      className={`h-[48px] w-[48px] rounded-2xl flex items-center justify-center shrink-0 transition-all duration-200 ${
                        input.trim() && !isLoading
                          ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-200 active:scale-95'
                          : 'bg-gray-200/40 text-gray-400 cursor-not-allowed'
                      }`}
                      aria-label="ارسال پیام"
                    >
                      <BsSend size={17} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between gap-2 px-1">
                    <p className="text-[10px] text-gray-500 shrink-0">
                      Enter ارسال &nbsp;·&nbsp; Shift+Enter خط جدید
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setSearchWeb(p => !p)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium backdrop-blur-xl border transition-all duration-200 select-none ${
                          searchWeb
                            ? 'bg-blue-500/20 border-blue-400 text-blue-700 shadow-sm'
                            : 'bg-white/20 border-white/30 text-gray-600 hover:bg-white/30'
                        }`}
                      >
                        <BsSearch size={12} />
                        <span>جستجوی وب</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setDeepSearch(p => !p)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium backdrop-blur-xl border transition-all duration-200 select-none ${
                          deepSearch
                            ? 'bg-indigo-500/20 border-indigo-400 text-indigo-700 shadow-sm'
                            : 'bg-white/20 border-white/30 text-gray-600 hover:bg-white/30'
                        }`}
                      >
                        <BsLightbulb size={12} />
                        <span>تفکر عمیق</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default AIPage;